<template>
    <div class="space-y-4">
        <!-- Stats Overview -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
                :title="t('dashboard.teacher.myClasses')"
                :value="data.stats.myClasses || 0"
                :icon="Home"
                color="blue"
            />
            <StatCard
                :title="t('dashboard.teacher.mySubjects')"
                :value="data.stats.mySubjects || 0"
                :icon="BookOpen"
                color="green"
            />
            <StatCard
                :title="t('dashboard.teacher.totalMaterials')"
                :value="data.stats.totalMaterials || 0"
                :icon="FileText"
                color="purple"
            />
            <StatCard
                :title="t('dashboard.teacher.publishedMaterials')"
                :value="data.stats.publishedMaterials || 0"
                :icon="CheckCircle"
                color="indigo"
            />
        </div>

        <!-- Active Exams and Recent Results -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <!-- Active/Recent Exams -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div class="px-4 py-4 sm:p-5">
                    <h3
                        class="text-base leading-6 font-medium text-gray-900 dark:text-white mb-3"
                    >
                        {{ t("dashboard.teacher.activeRecentExams") }}
                    </h3>
                    <div class="space-y-2">
                        <div
                            v-for="exam in data?.activeExams || []"
                            :key="exam.id"
                            class="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        :class="[
                                            'w-8 h-8 rounded-full flex items-center justify-center',
                                            getExamStatusColor(exam.status),
                                        ]"
                                    >
                                        <CircleHelp
                                            :class="[
                                                'w-4 h-4',
                                                getExamStatusIconColor(
                                                    exam.status,
                                                ),
                                            ]"
                                        />
                                    </div>
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ exam.title }}
                                    </p>
                                    <p
                                        class="text-sm text-gray-500 dark:text-gray-400"
                                    >
                                        {{ exam.subject }} • {{ exam.class }}
                                    </p>
                                    <div
                                        v-if="exam.startTime"
                                        class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                                    >
                                        {{ formatDateTime(exam.startTime) }}
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <span
                                    :class="[
                                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                                        getExamStatusClasses(exam.status),
                                    ]"
                                >
                                    {{ exam.status }}
                                </span>
                                <div
                                    v-if="exam.attemptsCount"
                                    class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                                >
                                    {{ exam.attemptsCount }}
                                    {{ t("dashboard.teacher.attempts") }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Recent Exam Results -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div class="px-4 py-4 sm:p-5">
                    <h3
                        class="text-base leading-6 font-medium text-gray-900 dark:text-white mb-3"
                    >
                        {{ t("dashboard.teacher.recentExamResults") }}
                    </h3>
                    <div class="space-y-2">
                        <div
                            v-for="result in data.recentExamResults"
                            :key="result.id"
                            class="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <div
                                        :class="[
                                            'w-8 h-8 rounded-full flex items-center justify-center',
                                            getResultStatusColor(result.status),
                                        ]"
                                    >
                                        <BarChart3
                                            :class="[
                                                'w-4 h-4',
                                                getResultStatusIconColor(
                                                    result.status,
                                                ),
                                            ]"
                                        />
                                    </div>
                                </div>
                                <div class="ml-3">
                                    <p
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ result.examTitle }}
                                    </p>
                                    <p
                                        class="text-sm text-gray-500 dark:text-gray-400"
                                    >
                                        {{ result.subject }}
                                    </p>
                                    <div
                                        class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                                    >
                                        {{ formatDate(result.completedAt) }}
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ result.score }}/{{ result.maxScore }}
                                </div>
                                <div class="flex items-center">
                                    <div
                                        class="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-2"
                                    >
                                        <div
                                            :class="[
                                                'h-2 rounded-full',
                                                getResultProgressBarColor(
                                                    result.percentage,
                                                ),
                                            ]"
                                            :style="{
                                                width: `${result.percentage}%`,
                                            }"
                                        ></div>
                                    </div>
                                    <span
                                        class="text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        {{ result.percentage }}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recent Materials -->
        <MaterialsSection
            :title="t('dashboard.teacher.recentMaterials')"
            :materials="data.recentMaterials || []"
            :showViewAll="true"
            viewAllRoute="/materials"
            :showEdit="true"
            :emptyStateTitle="t('dashboard.teacher.noMaterialsYet')"
            :emptyStateMessage="t('dashboard.teacher.createFirstMaterial')"
            @open="handleOpenMaterial"
            @edit="handleEditMaterial"
            @download="handleDownloadMaterial"
        />

        <!-- Quick Actions -->
        <QuickActions :actions="data?.quickActions || []" />
    </div>
</template>

<script setup lang="ts">
import {
    TeacherDashboardData,
    ExamItem,
    ExamResult,
    MaterialItem,
} from "@/types/dashboard";
import StatCard from "./StatCard.vue";
import QuickActions from "./QuickActions.vue";
import MaterialsSection from "./MaterialsSection.vue";
import materialApi from "@/api/material";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
    Home,
    BookOpen,
    CircleHelp,
    ClipboardList,
    BarChart3,
    FileText,
    CheckCircle,
} from "@lucide/vue";

const { t } = useI18n();

interface Props {
    data: TeacherDashboardData;
}

const props = defineProps<Props>();
const router = useRouter();

const handleViewMaterial = (material: MaterialItem) => {
    // Open material based on type
    if (material.type === "url" && material.url) {
        // Open URL in new tab
        window.open(material.url, "_blank");
    } else if (material.type === "file" && material.fileUrl) {
        // Open file in new tab
        window.open(material.fileUrl, "_blank");
    } else {
        // Fallback: navigate to materials page
        router.push("/materials");
    }
};

const handleEditMaterial = (material: MaterialItem) => {
    // Navigate to edit material page
    router.push(`/materials/${material.id}/edit`);
};

const handleDownloadMaterial = async (material: MaterialItem) => {
    try {
        // Import materialApi dynamically to avoid circular dependencies
        const { materialApi } = await import("@/api/material");

        // Download the material
        const blob = await materialApi.download(material.id);

        // Create a temporary link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = material.title || "material";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Failed to download material:", error);
        // You could add a toast notification here
    }
};

const handleOpenMaterial = (material: MaterialItem) => {
    // Open material based on type
    if (material.type === "url" && material.url) {
        // Open URL in new tab
        window.open(material.url, "_blank");
    } else if (material.type === "file" && material.fileUrl) {
        // Open file in new tab
        window.open(material.fileUrl, "_blank");
    }
};

const getExamStatusColor = (status: string) => {
    const colors = {
        draft: "bg-gray-100 dark:bg-gray-900",
        published: "bg-blue-100 dark:bg-blue-900",
        scheduled: "bg-indigo-100 dark:bg-indigo-900",
        active: "bg-green-100 dark:bg-green-900",
        completed: "bg-purple-100 dark:bg-purple-900",
    };
    return colors[status as keyof typeof colors] || colors.draft;
};

const getExamStatusIconColor = (status: string) => {
    const colors = {
        draft: "text-gray-600 dark:text-gray-300",
        published: "text-blue-600 dark:text-blue-300",
        scheduled: "text-indigo-600 dark:text-indigo-300",
        active: "text-green-600 dark:text-green-300",
        completed: "text-purple-600 dark:text-purple-300",
    };
    return colors[status as keyof typeof colors] || colors.draft;
};

const getExamStatusClasses = (status: string) => {
    const classes = {
        draft: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300",
        published:
            "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
        scheduled:
            "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300",
        active: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
        completed:
            "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300",
    };
    return classes[status as keyof typeof classes] || classes.draft;
};

const getResultStatusColor = (status: string) => {
    const colors = {
        passed: "bg-green-100 dark:bg-green-900",
        failed: "bg-red-100 dark:bg-red-900",
        pending: "bg-yellow-100 dark:bg-yellow-900",
    };
    return colors[status as keyof typeof colors] || colors.pending;
};

const getResultStatusIconColor = (status: string) => {
    const colors = {
        passed: "text-green-600 dark:text-green-300",
        failed: "text-red-600 dark:text-red-300",
        pending: "text-yellow-600 dark:text-yellow-300",
    };
    return colors[status as keyof typeof colors] || colors.pending;
};

const getResultProgressBarColor = (percentage: number) => {
    if (percentage >= 80) return "bg-green-500";
    if (percentage >= 60) return "bg-yellow-500";
    return "bg-red-500";
};

const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};
</script>
